import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import { useAuthStore } from '../../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import type { FieldDefinition } from '../types';

function meetsDependsOn(
  dep: FieldDefinition['dependsOn'],
  values: Record<string, string>,
): boolean {
  if (!dep) return true;
  const current = values[dep.field];
  if (Array.isArray(dep.value)) {
    return dep.value.some(
      v =>
        v === current ||
        (v === 'true' && current === 'true') ||
        (v === 'false' && current === 'false'),
    );
  }
  return (
    current === dep.value ||
    (dep.value === 'true' && current === 'true') ||
    (dep.value === 'false' && current === 'false')
  );
}

function buildIntegrationsConnectBody(
  provider: string,
  connectionName: string,
  values: Record<string, string>,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    mode: 'credentials',
    connectionName: connectionName.trim() || undefined,
  };
  if (provider !== 'mcp') {
    return { ...base, ...values };
  }
  const transport = values.transport;
  if (transport === 'remote_http') {
    return {
      ...base,
      transport: 'remote_http',
      serverUrl: values.serverUrl ?? '',
      authToken: values.authToken ?? '',
    };
  }
  if (transport === 'stdio_npx') {
    return {
      ...base,
      transport: 'stdio_npx',
      stdioPackage: values.stdioPackage ?? '',
      stdioPackageVersion: values.stdioPackageVersion ?? '',
      stdioSecrets: values.stdioSecrets ?? '',
    };
  }
  return { ...base, ...values };
}

interface CredentialsFormProps {
  provider: string;
  providerName: string;
  fields: FieldDefinition[];
  onSuccess?: (integrationId: string, name: string) => void;
  disabled?: boolean;
}

const CredentialsForm: React.FC<CredentialsFormProps> = ({
  provider,
  providerName,
  fields,
  onSuccess,
  disabled,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const { addConnection, fetchConnections } = useStoreThirdPartyIntegration();

  const [connectionName, setConnectionName] = useState('');
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach(f => {
      initial[f.name] = (f.defaultValue as string) ?? '';
    });
    return initial;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [awaitingOAuth, setAwaitingOAuth] = useState(false);
  const pendingNameRef = useRef('');

  useEffect(() => {
    if (!awaitingOAuth) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'mcp_oauth_completed') return;
      setAwaitingOAuth(false);
      setIsSubmitting(false);
      if (event.data.success) {
        const integrationId: string = event.data.integrationId ?? '';
        const name = pendingNameRef.current || providerName;
        if (integrationId) {
          addConnection(provider, {
            id: integrationId,
            name,
            provider,
            ownerUserId: user?.id ?? '',
            ownerUserName: user?.name ?? user?.email ?? '',
            isOwn: true,
          });
        }
        void fetchConnections(provider);
        setNotification({
          type: NotificationType.Success,
          message: 'Connected successfully',
        });
        if (integrationId) {
          onSuccess?.(integrationId, name);
        }
      } else {
        setNotification({
          type: NotificationType.Error,
          message: event.data.error ?? 'OAuth connection failed',
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [awaitingOAuth, provider, providerName, user, addConnection, fetchConnections, onSuccess, setNotification]);

  const handleChange = (name: string, value: unknown) => {
    setValues(prev => ({ ...prev, [name]: String(value ?? '') }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await ThirdPartyIntegrationService.connect(
        provider,
        buildIntegrationsConnectBody(provider, connectionName, values),
      );
      const result = response.data as Record<string, unknown>;

      if (result?.needsOAuth === true && typeof result.authorizeUrl === 'string') {
        pendingNameRef.current = connectionName || providerName;
        window.open(result.authorizeUrl, 'mcpOAuthPopup', 'popup,width=520,height=640');
        setAwaitingOAuth(true);
        return;
      }

      const integrationId = (result?.integrationId as string) ?? '';
      const name = (result?.name as string) ?? (connectionName || providerName);

      if (integrationId) {
        addConnection(provider, {
          id: integrationId,
          name,
          provider,
          ownerUserId: user?.id ?? '',
          ownerUserName: user?.name ?? user?.email ?? '',
          isOwn: true,
        });
      }

      await fetchConnections(provider);
      onSuccess?.(integrationId, name);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      if (!awaitingOAuth) setIsSubmitting(false);
    }
  };

  const inputSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiOutlinedInput-root': {
      bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
      '& fieldset': {
        borderColor: user?.theme === 'dark' ? '#475467' : '#D0D5DD',
      },
    },
  };

  const selectSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    borderRadius: '8px',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
  };

  const labelSx = {
    display: 'block',
    fontSize: '14px',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    fontWeight: 400,
    mb: 0.5,
  };

  const helperTextSx = {
    display: 'block',
    fontSize: '12px',
    lineHeight: 1.45,
    color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
    mt: 0.75,
    maxWidth: '360px',
  };

  const visibleFields = useMemo(
    () => fields.filter(f => meetsDependsOn(f.dependsOn, values)),
    [fields, values],
  );

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box>
        <Box component="label" sx={labelSx}>
          Connection Name
        </Box>
        <TextField
          className="nodrag"
          size="small"
          fullWidth
          placeholder={`e.g. My ${providerName}`}
          value={connectionName}
          onChange={e => setConnectionName(e.target.value)}
          disabled={disabled}
          sx={inputSx}
        />
      </Box>

      {visibleFields.map(field => {
        const fieldLabel = (
          <Box component="label" sx={labelSx}>
            {field.label}
            {field.validation?.required && ' *'}
          </Box>
        );

        if (field.type === 'select' && Array.isArray(field.options)) {
          const options = field.options;
          const current = values[field.name] ?? '';
          const selectedLabel = options.find(o => o.value === current)?.label;
          return (
            <Box key={field.name}>
              {fieldLabel}
              <Select
                className="nodrag"
                size="small"
                fullWidth
                value={current}
                onChange={e => handleChange(field.name, e.target.value)}
                disabled={disabled}
                required={field.validation?.required}
                sx={selectSx}
                displayEmpty
                renderValue={() =>
                  selectedLabel ?? field.placeholder ?? `Select ${field.label}`
                }
              >
                {options.map(opt => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black' }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {field.helperText ? (
                <Typography component="span" sx={helperTextSx}>
                  {field.helperText}
                </Typography>
              ) : null}
            </Box>
          );
        }

        if (field.type === 'textarea') {
          const maskAsSecret =
            field.name.toLowerCase().includes('token') ||
            field.name.toLowerCase().includes('password') ||
            field.name.toLowerCase().includes('secret');
          return (
            <Box key={field.name}>
              {fieldLabel}
              <TextField
                className="nodrag"
                size="small"
                fullWidth
                multiline
                minRows={3}
                type={maskAsSecret ? 'password' : 'text'}
                placeholder={field.placeholder}
                value={values[field.name] ?? ''}
                onChange={e => handleChange(field.name, e.target.value)}
                disabled={disabled}
                required={field.validation?.required}
                inputProps={{ maxLength: field.validation?.maxLength }}
                sx={inputSx}
              />
              {field.helperText ? (
                <Typography component="span" sx={helperTextSx}>
                  {field.helperText}
                </Typography>
              ) : null}
            </Box>
          );
        }

        const isSecretField =
          field.name.toLowerCase().includes('token') ||
          field.name.toLowerCase().includes('password') ||
          field.name.toLowerCase().includes('secret');

        return (
          <Box key={field.name}>
            {fieldLabel}
            <TextField
              className="nodrag"
              size="small"
              fullWidth
              type={isSecretField ? 'password' : 'text'}
              placeholder={field.placeholder}
              value={values[field.name] ?? ''}
              onChange={e => handleChange(field.name, e.target.value)}
              disabled={disabled}
              required={field.validation?.required}
              inputProps={{ maxLength: field.validation?.maxLength }}
              sx={inputSx}
            />
            {field.helperText ? (
              <Typography component="span" sx={helperTextSx}>
                {field.helperText}
              </Typography>
            ) : null}
          </Box>
        );
      })}
      <Button
        type="submit"
        variant="contained"
        size="small"
        disabled={disabled || isSubmitting}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        {awaitingOAuth
          ? 'Waiting for authorization...'
          : isSubmitting
            ? 'Connecting...'
            : `Connect to ${providerName}`}
      </Button>
    </Box>
  );
};

export default CredentialsForm;
